import {
  Notification02Icon as Notification03SolidIcon,
  UserMultiple02Icon as UserMultipleSolidIcon,
  GridViewIcon as GridViewSolidIcon,
  MessageMultiple01Icon as MessageMultiple01SolidIcon,
  Home01Icon as Home01SolidIcon,
} from '@hugeicons-pro/core-solid-rounded';
import {
  UserMultiple02Icon,
  GridViewIcon,
  MessageMultiple01Icon,
  Home01Icon,
  Notification02Icon,
} from '@hugeicons-pro/core-stroke-rounded';
import { Tabs, usePathname, useRouter } from 'expo-router';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, Pressable } from 'react-native';

import {
  getDiscoverListHref,
  getDiscoverListItems,
  getDiscoverListTypeFromPathname,
} from '@/components/domain/discover/discoverListItems';
import {
  getPeopleListHref,
  getPeopleListItems,
  getPeopleListTypeFromPathname,
} from '@/components/domain/members/peopleListItems';
import {
  getMessageListHref,
  getMessageListItems,
  getMessageListTypeFromPathname,
} from '@/components/domain/messages/messageListItems';
import {
  getFeedListHref,
  getFeedListItems,
  getFeedTypeFromPathname,
} from '@/components/domain/posts/feedListItems';
import { HStack } from '@/components/gluestack/hstack';
import { VStack } from '@/components/gluestack/vstack';
import { AnimatedTabBar, TabBarVisibilityProvider } from '@/components/layout/AnimatedTabBar';
import { LargeScreenHeader } from '@/components/layout/LargeScreenHeader';
import { LargeScreenLeftPanel } from '@/components/layout/LargeScreenLeftPanel';
import { LargeScreenRightPanel } from '@/components/layout/LargeScreenRightPanel';
import { RightPanelSlotProvider } from '@/components/layout/RightPanelSlotContext';
import { TabBarSlideMenuButton } from '@/components/layout/TabBarSlideMenuButton';
import { ConnectionBanner } from '@/components/ui/ConnectionBanner';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import { TabBadge } from '@/components/ui/TabBadge';
import { UpdateBanner } from '@/components/ui/UpdateBanner';
import { clearStoredScrollPosition } from '@/hooks/useInfiniteListUtils';
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';
import { useWebKeyboard } from '@/hooks/useKeyboard';
import { useSignalRUpdates } from '~/components/providers/SignalRProvider';
import { useTheme } from '~/components/providers/ThemeProvider';
import { useVersionCheck } from '~/components/providers/VersionCheckProvider';
import {
  getSwitchingToCommunity,
  setSwitchingToCommunity,
} from '~/libs/navigation/communitySwitchStore';
export { RouteErrorBoundary as ErrorBoundary } from '~/components/providers/AppErrorBoundary';

const iconConfig = { size: 28 };

// How long the freshly mounted post-switch tree gets to natively present
// before the switch overlay starts fading (JS frame fences underestimate it).
const OVERLAY_SETTLE_GRACE_MS = 350;

// Subscribe to the volatile SignalR counts down here, not in AppLayout itself:
// a new message/notification then re-renders only the badge, not the whole Tabs tree.
const SignalRTabBadge = ({
  counter,
  children,
}: {
  counter: 'messageCount' | 'notificationCount';
  children: React.ReactNode;
}) => {
  const counts = useSignalRUpdates();
  return <TabBadge count={counts[counter]}>{children}</TabBadge>;
};

export default function AppLayout() {
  const { t } = useTranslation();
  const { effectiveColorScheme, getThemeColor } = useTheme();

  // Destination side of the community-switch overlay: this layout mounting
  // means the fresh post-reset tree exists, so start dropping the cover a
  // fixed settle-grace later. A frame-count fence is NOT enough here —
  // native presentation of the freshly attached tree lags the JS commit
  // (rAFs fire on JS frames), and clearing on it let a white not-yet-
  // presented frame peek through. Mount-only on purpose (store reads, no
  // subscription): when the target is set at switch start, the OLD tree's
  // still-mounted layout must not schedule a clear mid-switch — and this
  // layout doesn't re-render on switch flips at all.
  useEffect(() => {
    if (!getSwitchingToCommunity()) return;
    const settleTimer = setTimeout(() => setSwitchingToCommunity(null), OVERLAY_SETTLE_GRACE_MS);
    return () => clearTimeout(settleTimer);
  }, []);
  const isLargeScreen = useIsLargeScreen();
  const { newVersionAvailable, reloadPage } = useVersionCheck();
  const { isWebKeyboardVisible } = useWebKeyboard();
  const pathname = usePathname();
  const router = useRouter();
  const tabBarActiveTintColor = getThemeColor('tertiary', '500');
  const tabBarInactiveTintColor = effectiveColorScheme === 'dark' ? '#888888' : '#555555';
  const hideTabBar = isLargeScreen || isWebKeyboardVisible;
  const peopleTabItems = useMemo(() => getPeopleListItems(t), [t]);
  const activePeopleList = getPeopleListTypeFromPathname(pathname);
  const feedTabItems = useMemo(() => getFeedListItems(t), [t]);
  const activeFeed = getFeedTypeFromPathname(pathname);
  const discoverTabItems = useMemo(() => getDiscoverListItems(t), [t]);
  const activeDiscoverList = getDiscoverListTypeFromPathname(pathname);
  const messageTabItems = useMemo(() => getMessageListItems(t), [t]);
  const activeMessageList = getMessageListTypeFromPathname(pathname);

  return (
    <RightPanelSlotProvider>
      <VStack className="flex-1">
        {/* Large Screen Header - only shown on large screens */}
        {isLargeScreen && <LargeScreenHeader />}
        {isLargeScreen && newVersionAvailable && <UpdateBanner onReload={reloadPage} />}
        {isLargeScreen && <ConnectionBanner />}

        <HStack className="flex-1">
          {/* Large Screen Left Panel - only shown on large screens */}
          {isLargeScreen && <LargeScreenLeftPanel />}

          {/* Main Content Area with Tabs */}
          <VStack className="flex-1">
            <TabBarVisibilityProvider>
              <Tabs
                // Track visited tabs in history so the browser back button
                // reflects the order they were visited. With the default
                // `firstRoute`, the tab navigator's internal history length
                // stays at 2 when switching between non-first tabs, which
                // causes React Navigation's web linking to call
                // `history.replaceState` (overwriting the previous tab's URL
                // in the browser history) instead of `pushState`.
                backBehavior="history"
                tabBar={
                  hideTabBar || Platform.OS === 'web'
                    ? undefined
                    : (props) => <AnimatedTabBar {...props} />
                }
                screenOptions={() => ({
                  headerShown: false,
                  // Freezing blurred tabs races the native screen detach under
                  // fast tab switches (react-native-screens + Fabric) — the
                  // outgoing tab's view stays attached and covers tabs declared
                  // before it, so a tab can show another tab's screen (or stop
                  // switching at all) until restart. Seen on iOS (6.1.17), then
                  // Android (6.1.18); web never freezes (Screen.web ignores it).
                  // The per-tab stacks keep freezeOnBlur (6.0.178 perf win).
                  freezeOnBlur: false,
                  tabBarActiveTintColor,
                  tabBarInactiveTintColor,
                  tabBarShowLabel: false,
                  tabBarStyle: hideTabBar ? { display: 'none' } : undefined,
                  tabBarIconStyle: {
                    height: 24,
                    marginTop: 6,
                    marginBottom: 4,
                  },
                  tabBarButton: hideTabBar ? () => null : undefined,
                })}>
                <Tabs.Screen
                  name="(feeds)"
                  options={{
                    title: 'Feeds',
                    tabBarLabel: 'Feeds',
                    tabBarButton: hideTabBar
                      ? () => null
                      : (buttonProps) => (
                          <TabBarSlideMenuButton
                            buttonProps={buttonProps}
                            tabName="(feeds)"
                            items={feedTabItems}
                            value={activeFeed}
                            onSelect={(nextFeed) => {
                              const href = getFeedListHref(nextFeed, activeFeed);
                              clearStoredScrollPosition(href);
                              if (router.canDismiss()) {
                                router.dismissAll();
                              }
                              router.replace(href as any);
                            }}
                          />
                        ),
                    tabBarIcon: ({ color, focused }) => (
                      <HugeIconsWrapper
                        icon={Home01Icon}
                        altIcon={Home01SolidIcon}
                        color={color}
                        showAlt={focused}
                        {...iconConfig}
                      />
                    ),
                  }}
                />
                <Tabs.Screen
                  name="(people)"
                  options={{
                    title: 'People',
                    tabBarLabel: 'People',
                    tabBarButton: hideTabBar
                      ? () => null
                      : (buttonProps) => (
                          <TabBarSlideMenuButton
                            buttonProps={buttonProps}
                            tabName="(people)"
                            items={peopleTabItems}
                            value={activePeopleList}
                            onColdTabSelect={(nextList) => {
                              const href = getPeopleListHref(nextList);
                              clearStoredScrollPosition(href);
                              router.replace(href as any, { withAnchor: false });
                            }}
                            onSelect={(nextList) => {
                              const href = getPeopleListHref(nextList);
                              clearStoredScrollPosition(href);
                              if (router.canDismiss()) {
                                router.dismissAll();
                              }
                              router.replace(href as any);
                            }}
                          />
                        ),
                    tabBarIcon: ({ color, focused }) => (
                      <HugeIconsWrapper
                        icon={UserMultiple02Icon}
                        altIcon={UserMultipleSolidIcon}
                        color={color}
                        showAlt={focused}
                        {...iconConfig}
                      />
                    ),
                  }}
                />
                <Tabs.Screen
                  name="(discover)"
                  options={{
                    title: 'Discover',
                    tabBarLabel: 'Discover',
                    tabBarButton: hideTabBar
                      ? () => null
                      : (buttonProps) => (
                          <TabBarSlideMenuButton
                            buttonProps={buttonProps}
                            tabName="(discover)"
                            items={discoverTabItems}
                            value={activeDiscoverList}
                            onSelect={(nextValue) => {
                              const href = getDiscoverListHref(nextValue);
                              clearStoredScrollPosition(href);
                              if (router.canDismiss()) {
                                router.dismissAll();
                              }
                              router.push(href as any);
                            }}
                          />
                        ),
                    tabBarIcon: ({ color, focused }) => (
                      <HugeIconsWrapper
                        icon={GridViewIcon}
                        altIcon={GridViewSolidIcon}
                        color={color}
                        showAlt={focused}
                        {...iconConfig}
                      />
                    ),
                  }}
                />
                <Tabs.Screen
                  name="(messages)"
                  options={{
                    title: 'Messages',
                    tabBarLabel: 'Messages',
                    tabBarButton: hideTabBar
                      ? () => null
                      : (buttonProps) => (
                          <TabBarSlideMenuButton
                            buttonProps={buttonProps}
                            tabName="(messages)"
                            items={messageTabItems}
                            value={activeMessageList}
                            onColdTabSelect={(nextList) => {
                              const href = getMessageListHref(nextList);
                              clearStoredScrollPosition(href);
                              router.replace(href as any, { withAnchor: false });
                            }}
                            onSelect={(nextList) => {
                              const href = getMessageListHref(nextList);
                              clearStoredScrollPosition(href);
                              if (router.canDismiss()) {
                                router.dismissAll();
                              }
                              router.replace(href as any);
                            }}
                          />
                        ),
                    tabBarIcon: ({ color, focused }) => (
                      <SignalRTabBadge counter="messageCount">
                        <HugeIconsWrapper
                          icon={MessageMultiple01Icon}
                          altIcon={MessageMultiple01SolidIcon}
                          color={color}
                          showAlt={focused}
                          {...iconConfig}
                        />
                      </SignalRTabBadge>
                    ),
                  }}
                />
                <Tabs.Screen
                  name="(notifications)"
                  options={{
                    title: 'Notifications',
                    tabBarLabel: 'Notifications',
                    // The default tab button shows a material ripple on Android; the other
                    // tabs use TabBarSlideMenuButton (plain Pressable), so match its
                    // press feedback here.
                    tabBarButton: hideTabBar
                      ? () => null
                      : Platform.OS === 'android'
                        ? (buttonProps) => (
                            <Pressable
                              accessible
                              accessibilityLabel={buttonProps.accessibilityLabel}
                              accessibilityRole={buttonProps.accessibilityRole}
                              accessibilityState={buttonProps.accessibilityState}
                              onPress={buttonProps.onPress}
                              onLongPress={buttonProps.onLongPress}
                              testID={buttonProps.testID}
                              style={[buttonProps.style, { flex: 1 }]}>
                              {buttonProps.children}
                            </Pressable>
                          )
                        : undefined,
                    tabBarIcon: ({ color, focused }) => (
                      <SignalRTabBadge counter="notificationCount">
                        <HugeIconsWrapper
                          icon={Notification02Icon}
                          altIcon={Notification03SolidIcon}
                          color={color}
                          showAlt={focused}
                          {...iconConfig}
                        />
                      </SignalRTabBadge>
                    ),
                  }}
                />

                <Tabs.Screen
                  name="+not-found"
                  options={{
                    href: null, // hides tab
                  }}
                />
              </Tabs>
            </TabBarVisibilityProvider>
          </VStack>

          {/* Large Screen Right Panel - slot-based, only shown on large screens when a screen provides content */}
          {isLargeScreen && <LargeScreenRightPanel />}
        </HStack>
      </VStack>
    </RightPanelSlotProvider>
  );
}
